import { Injectable } from '@angular/core';

import { AuthErrorType } from 'src/app/resources/authentication/models';
import { FormPayload } from 'src/lib/forms';
import { AdobeAnalyticsService } from './AdobeAnalyticsService';
import { AnalyticsEvent, AnalyticsEventType } from './model';



@Injectable({providedIn: 'root'})
export class AnalyticsService {

  constructor(private adobeAnalytics: AdobeAnalyticsService) {}


  //=============================================
  //  Client side form validation
  //---------------------------------------------

  trackFormError(formPayload: FormPayload): void {
    const { errors, formName } = formPayload;

    if (Array.isArray(errors) && errors.length) {
      const { message, scope } = errors[0];

      const event = new AnalyticsEvent({
        type: AnalyticsEventType.Error,
        description: `fe|${formName}|${scope}|${message}`
      });

      this.adobeAnalytics.track(event);
    }
  }


  //=============================================
  //  One time password
  //---------------------------------------------

  trackPasswordResetOtpResponse(code, resend: boolean, method: string): void {
    this.trackOtpResponse('password_reset', code, resend, method);
  }

  trackSignInOtpResponse(code, resend: boolean, method: string): void {
    this.trackOtpResponse('sign_in', code, resend, method);
  }

  trackUsernameRecoveryOtpResponse(code, resend: boolean, method: string): void {
    this.trackOtpResponse('username_recovery', code, resend, method);
  }

  private trackOtpResponse(context: string, code, resend: boolean, method: string): void {
    let description = resend ? 'otp_resent' : 'otp_sent';
    switch (method) {
      case '1':
      case 'SMS':
        description += '|sms';
        break;
      case '2':
      case 'EMAIL':
        description += '|email';
        break;
      case '3':
        description += '|voice';
        break;
    }

    const event = new AnalyticsEvent({
      type: AnalyticsEventType.Interaction,
      description: `${context}|${code}|${description}`
    });

    this.adobeAnalytics.track(event);
  }


  //=============================================
  //  Password reset
  //---------------------------------------------

  trackPasswordResetError(error): void {
    const event = new AnalyticsEvent({
      type: AnalyticsEventType.Error,
      description: `be|password_reset|${error.code}|${AuthErrorType[error.code] || 'unknown'}`
    });

    this.adobeAnalytics.track(event);
  }

  trackPasswordResetResponse(code): void {
    let description = `be|password_reset|${code}|`;
    let event;

    switch (code) {
      case '10':
        description += 'verification_required';
        break;
      case '12':
        description += 'otp_sent|email';
        break;
      case '50':
        description += 'success';
        break;
    }

    event = new AnalyticsEvent({
      type: AnalyticsEventType.Interaction,
      description: description
    });

    this.adobeAnalytics.track(event);
  }


  //=============================================
  //  Password update
  //---------------------------------------------

  trackPasswordUpdateError(error): void {
    const event = new AnalyticsEvent({
      type: AnalyticsEventType.Error,
      description: `be|password_update|${error.code}|${AuthErrorType[error.code] || 'unknown'}`
    });

    this.adobeAnalytics.track(event);
  }

  trackPasswordUpdateSuccess(): void {
    const event = new AnalyticsEvent({
      type: AnalyticsEventType.Interaction,
      description: 'be|password_update|success'
    });

    this.adobeAnalytics.track(event);
  }


  //=============================================
  //  Sign in
  //---------------------------------------------

  trackSignInError(error): void {
    const event = new AnalyticsEvent({
      type: AnalyticsEventType.SignInError,
      description: `be|sign_in|${error?.code}|${AuthErrorType[error?.code] || 'unknown'}`
    });

    this.adobeAnalytics.track(event);
  }

  trackSignInVerificationMethodSelected(method: string): void {
    const event = new AnalyticsEvent({
      type: AnalyticsEventType.Interaction,
      description: `fe|sign_in|2fv_method_selected|${(method || 'unknown').toLowerCase()}`
    });

    this.adobeAnalytics.track(event);
  }

  trackSignInResponse(code): void {
    let description;
    let event;

    if (code === '10') {
      event = new AnalyticsEvent({
        type: AnalyticsEventType.Error,
        description: `be|sign_in|${code}|verification_required`
      });
    }
    else {
      switch (code) {
        case '1':
          description = 'success';
          break;
        case '20':
          description = 'success|2fv_opt_in_required';
          break;
        case '40':
          description = 'success|step_up_required';
          break;
        case '60':
          description = 'success|terms_acceptance_required';
          break;
      }

      event = new AnalyticsEvent({
        type: AnalyticsEventType.SignInSuccess,
        description: `be|sign_in|${code}|${description}`
      });
    }

    this.adobeAnalytics.track(event);
  }


  //=============================================
  //  Username recovery
  //---------------------------------------------

  trackUsernameRecoveryError(error): void {
    const event = new AnalyticsEvent({
      type: AnalyticsEventType.Error,
      description: `be|username_recovery|${error.code}|${AuthErrorType[error.code] || 'unknown'}`
    });

    this.adobeAnalytics.track(event);
  }

  trackUsernameRecoveryResponse(code): void {
    let description = `be|username_recovery|${code}|`;
    let event;

    switch (code) {
      case '10':
        description += 'verification_required';
        break;
      case '12':
        description += 'otp_sent|email';
        break;
      case '30':
        description += 'username_sent|email';
        break;
      case '50':
        description += 'success';
        break;
    }

    event = new AnalyticsEvent({
      type: AnalyticsEventType.Interaction,
      description: description
    });

    this.adobeAnalytics.track(event);
  }

}
